import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SkipNextOutlinedIcon from '@mui/icons-material/SkipNextOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GenericButton, { GenericButtonTypeEnum } from '../../../../components/GenericButton';
import MonthStepper from '../../../../components/MonthStepper';
import { MESSAGE_TYPE } from '../../../../common/constants/common.constants';
import { FINANCE_CONTRACTS } from '../../../../common/constants/routes.constants';
import { GenerationPreviewOutcomeEnum } from '../../../../common/enums/invoicing.enum';
import { GenerationPreviewDto, InvoiceBatchDto } from '../../../../common/interfaces/invoicing.interface';
import { generateInvoiceBatch, getGenerationPreview } from '../../../../api/invoicing.api';
import { getGroupsWithLocationName } from '../../../../api/kindergarten.api';
import { createNotification } from '../../../../utilities/helper/notification.helper';
import { formatMoney } from '../../../../utilities/helper/money.helper';
import { formatMonthKey, formatSerbianDate, parseMonthKey } from '../../../../utilities/helper/date.helper';
import { missingList } from '../Contracts/contractText';

interface GenerateDraftsDialogProps {
  kindergartenId: string;
  /** `YYYY-MM`, the month the list is on. */
  month: string;
  onClose: () => void;
  onGenerated: (batch: InvoiceBatchDto) => void;
}

/**
 * Drafts for every child who is ready, for one month.
 *
 * Says what will happen before it happens, from a dry run on the server: who gets a draft,
 * who already has the month's invoice, who is not ready and why, and who joined after the
 * 1st (billed the full month all the same). Only the children the preview says will get a
 * draft are sent, so the run cannot do something the preview did not show.
 */
const GenerateDraftsDialog: React.FC<GenerateDraftsDialogProps> = ({ kindergartenId, month: initialMonth, onClose, onGenerated }) => {
  const { t } = useTranslation();
  const [month, setMonth] = useState(initialMonth);
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [preview, setPreview] = useState<GenerationPreviewDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    getGroupsWithLocationName(kindergartenId)
      .then(result => setGroups((result.results ?? []).map((group: any) => ({ id: group.id, name: group.name }))))
      .catch(() => createNotification(MESSAGE_TYPE.ERROR, t('groups.listLoadFailed')));
  }, [kindergartenId, t]);

  useEffect(() => {
    const parsed = parseMonthKey(month);
    if (!parsed) return undefined;
    let cancelled = false;
    setLoading(true);
    // the run sends the preview's children, so a preview for another month or other groups
    // must never outlive the request that replaces it
    setPreview(null);
    getGenerationPreview(kindergartenId, { periodYear: parsed.year, periodMonth: parsed.month, groupIds: groupIds.length ? groupIds : undefined })
      .then(result => !cancelled && setPreview(result.results ?? null))
      .catch(() => {
        if (cancelled) return;
        setPreview(null);
        createNotification(MESSAGE_TYPE.ERROR, t('invoices.generateMonth.previewFailed'));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // the groups array is rebuilt on every change; its joined form is the dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindergartenId, month, groupIds.join(), t]);

  const creating = preview?.children.filter(child => child.outcome === GenerationPreviewOutcomeEnum.CREATE) ?? [];
  const skippedHas = preview?.children.filter(child => child.outcome === GenerationPreviewOutcomeEnum.HAS_INVOICE) ?? [];
  const notReady = preview?.children.filter(child => child.outcome === GenerationPreviewOutcomeEnum.NOT_READY) ?? [];
  const lateJoiners = creating.filter(child => child.joinedMidMonth);
  const monthLabel = formatMonthKey(month).toLowerCase();

  const run = async () => {
    const parsed = parseMonthKey(month);
    if (!parsed || !creating.length) return;
    setRunning(true);
    try {
      const result = await generateInvoiceBatch(kindergartenId, {
        periodYear: parsed.year,
        periodMonth: parsed.month,
        childIds: creating.map(child => child.childId)
      });
      if (result.results) onGenerated(result.results);
      onClose();
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('invoices.generate.failed'));
    } finally {
      setRunning(false);
    }
  };

  const Row: React.FC<{ icon: React.ReactNode; title: string; children?: React.ReactNode }> = ({ icon, title, children }) => (
    <Stack component="li" direction="row" spacing={1.5} sx={{ listStyle: 'none' }}>
      <Box sx={{ pt: '2px' }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{title}</Typography>
        {children}
      </Box>
    </Stack>
  );

  return (
    <Dialog open onClose={running ? undefined : onClose} fullWidth slotProps={{ paper: { sx: { maxWidth: 640, borderRadius: 4 } } }}>
      <DialogTitle sx={{ fontSize: 20, fontWeight: 600 }}>{t('invoices.generateMonth.title', { month: monthLabel })}</DialogTitle>
      <DialogContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2.5 }} alignItems={{ sm: 'flex-start' }}>
          <Box>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 0.5 }}>{t('invoices.generateMonth.month')}</Typography>
            <MonthStepper value={month} onChange={setMonth} disabled={running} />
            {preview && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
                {formatSerbianDate(preview.periodStart)} – {formatSerbianDate(preview.periodEnd)}
              </Typography>
            )}
          </Box>
          <Autocomplete
            multiple
            options={groups}
            getOptionLabel={option => option.name}
            value={groups.filter(group => groupIds.includes(group.id))}
            onChange={(_, value) => setGroupIds(value.map(group => group.id))}
            renderTags={(value, getTagProps) => value.map((option, index) => <Chip {...getTagProps({ index })} key={option.id} size="small" label={option.name} />)}
            renderInput={params => <TextField {...params} label={t('common.groups')} placeholder={groupIds.length ? '' : t('invoices.generateMonth.allGroups')} />}
            disabled={running}
            sx={{ flex: 1 }}
          />
        </Stack>

        <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
          {t('invoices.generateMonth.whatHappens')}
        </Typography>
        {loading || !preview ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !preview.children.length ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            {t('invoices.generateMonth.nobodyEnrolled')}
          </Alert>
        ) : (
          <Stack component="ul" spacing={1.75} sx={{ m: 0, p: 0 }}>
            <Row
              icon={<CheckCircleOutlineIcon sx={{ fontSize: 20, color: 'success.main' }} />}
              title={t('invoices.generateMonth.willCreate', { count: creating.length })}
            >
              {creating.length > 0 && (
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                  {t('invoices.generateMonth.about', { amount: formatMoney(preview.estimatedTotal, { withCurrency: true }) })}
                  {preview.hasAttendanceItems ? ` ${t('invoices.generateMonth.plusAttendance')}` : ''}
                </Typography>
              )}
            </Row>
            {skippedHas.length > 0 && (
              <Row
                icon={<SkipNextOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />}
                title={t('invoices.generateMonth.hasInvoice', { count: skippedHas.length, month: monthLabel })}
              />
            )}
            {notReady.length > 0 && (
              <Row
                icon={<WarningAmberIcon sx={{ fontSize: 20, color: 'warning.main' }} />}
                title={t('invoices.generateMonth.notReady', { count: notReady.length })}
              >
                <Box component="ul" sx={{ m: 0, pl: 2, fontSize: 13, color: 'text.secondary' }}>
                  {notReady.map(child => (
                    <li key={child.childId}>
                      {t('invoices.generateMonth.notReadyChild', { name: child.childFullName, what: missingList(t, child.missing) })}
                    </li>
                  ))}
                </Box>
                <Link component={RouterLink} to={`${FINANCE_CONTRACTS}?kindergartenId=${kindergartenId}`} sx={{ fontSize: 13, color: 'primary.main' }}>
                  {t('invoices.steps.contracts.link')}
                </Link>
              </Row>
            )}
            {lateJoiners.length > 0 && (
              <Row
                icon={<InfoOutlinedIcon sx={{ fontSize: 20, color: 'info.main' }} />}
                title={t('invoices.generateMonth.lateJoiners', { count: lateJoiners.length })}
              >
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t('invoices.generateMonth.lateJoinersHint')}</Typography>
              </Row>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <GenericButton buttonType={GenericButtonTypeEnum.Neutral} variant="outlined" label={t('common.cancel')} onClick={onClose} disabled={running} />
        <GenericButton
          buttonType={GenericButtonTypeEnum.Primary}
          variant="contained"
          label={t('invoices.generateMonth.submit', { count: creating.length })}
          onAsyncAction={run}
          disabled={loading || running || !creating.length}
        />
      </DialogActions>
    </Dialog>
  );
};

export default GenerateDraftsDialog;
